import { Controller } from '@hotwired/stimulus'

// Subscribes the signed-in browser to Web Push (VAPID) and posts the
// subscription endpoint to Rails for order / chat / rider alerts.
export default class extends Controller {
  static values = {
    publicKey: String,
    subscribeUrl: String,
    unsubscribeUrl: String,
    subscribed: { type: Boolean, default: false }
  }

  static targets = ['status', 'enableButton', 'disableButton', 'prompt']

  connect () {
    this.syncUi()
    if (!this.supported()) return

    this.refreshPermissionState().then(() => {
      if (Notification.permission === 'granted') {
        this.ensureSubscription({ quiet: true })
      } else if (Notification.permission === 'default' && this.shouldAutoPrompt()) {
        this.showPrompt()
      }
    })
  }

  async enable (event) {
    event?.preventDefault()
    if (!this.supported()) {
      this.setStatus('Push alerts need a modern browser.')
      return
    }

    try {
      const permission = await Notification.requestPermission()
      if (permission !== 'granted') {
        this.setStatus('Permission blocked. Enable notifications in browser settings.')
        this.syncUi()
        return
      }

      await this.ensureSubscription({ quiet: false })
      this.hidePrompt()
      localStorage.setItem(this.promptKey(), 'dismissed')
    } catch (error) {
      console.warn('Web push enable failed', error)
      this.setStatus('Could not enable alerts. Try again.')
    }
  }

  async disable (event) {
    event?.preventDefault()
    try {
      const registration = await navigator.serviceWorker.ready
      const subscription = await registration.pushManager.getSubscription()
      if (subscription) {
        await this.postJson(this.unsubscribeUrlValue, {
          endpoint: subscription.endpoint
        }, 'DELETE')
        await subscription.unsubscribe()
      }
      this.subscribedValue = false
      this.setStatus('Push alerts turned off on this device.')
      this.syncUi()
    } catch (error) {
      console.warn('Web push disable failed', error)
      this.setStatus('Could not turn off alerts.')
    }
  }

  dismissPrompt (event) {
    event?.preventDefault()
    localStorage.setItem(this.promptKey(), 'dismissed')
    this.hidePrompt()
  }

  async ensureSubscription ({ quiet }) {
    const registration = await navigator.serviceWorker.ready
    let subscription = await registration.pushManager.getSubscription()

    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: this.urlBase64ToUint8Array(this.publicKeyValue)
      })
    }

    const payload = subscription.toJSON()
    await this.postJson(this.subscribeUrlValue, {
      endpoint: payload.endpoint,
      keys: payload.keys
    }, 'POST')

    this.subscribedValue = true
    if (!quiet) this.setStatus('Push alerts enabled on this device.')
    this.syncUi()
  }

  async refreshPermissionState () {
    if (!('permissions' in navigator)) return
    try {
      const status = await navigator.permissions.query({ name: 'notifications' })
      status.onchange = () => this.syncUi()
    } catch (_) {
      // Safari may not support notifications permission query.
    }
  }

  supported () {
    return (
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window &&
      !!this.publicKeyValue
    )
  }

  shouldAutoPrompt () {
    return localStorage.getItem(this.promptKey()) !== 'dismissed'
  }

  promptKey () {
    return 'ebazzari-push-prompt-v1'
  }

  showPrompt () {
    if (this.hasPromptTarget) this.promptTarget.classList.remove('hidden')
  }

  hidePrompt () {
    if (this.hasPromptTarget) this.promptTarget.classList.add('hidden')
  }

  syncUi () {
    const granted = this.supported() && Notification.permission === 'granted'
    const subscribed = granted && this.subscribedValue

    this.enableButtonTargets.forEach((el) => el.classList.toggle('hidden', subscribed))
    this.disableButtonTargets.forEach((el) => el.classList.toggle('hidden', !subscribed))
  }

  setStatus (message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message
    this.statusTarget.classList.toggle('hidden', !message)
  }

  async postJson (url, body, method) {
    const token = document.querySelector('meta[name="csrf-token"]')?.content
    const response = await fetch(url, {
      method,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-CSRF-Token': token || ''
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    })

    if (!response.ok && response.status !== 204) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.error || `HTTP ${response.status}`)
    }

    return response
  }

  urlBase64ToUint8Array (base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/')
    const raw = window.atob(base64)
    const output = new Uint8Array(raw.length)
    for (let i = 0; i < raw.length; i += 1) output[i] = raw.charCodeAt(i)
    return output
  }
}
